/**
 * camera_model — ONE physical camera for the player's view (2026-09-25,
 * visual-realism R4). The field of view, the depth of field, the motion-blur
 * shutter and the exposure offset all derive from the same lens + shutter +
 * ISO, the Frostbite / Filament position (a camera is part of the lighting
 * model, not three unrelated sliders).
 *
 * FIELD OF VIEW. three's `fov` is VERTICAL. The old default of 75° vertical is
 * 107.5° HORIZONTAL at 16:9 — a small, fast world. First person now defaults
 * to 60° vertical (≈ 91.5° horizontal at 16:9), third person to 55°. The menu
 * shows and sets the HORIZONTAL value at the 16:9 reference and stores the
 * vertical one: a fixed vertical FOV IS Hor+ (a wider screen sees more to the
 * sides, never less above and below). The sprint / dash FOV kick is capped at
 * 3° (a 6° kick read as a fish-eye lurch).
 *
 * DEPTH OF FIELD (render/dof/PhysicalDof.ts): off in first person, always —
 * the eye refocuses where you look, a vista is never a tilt-shift miniature;
 * in third person the lens autofocuses on the player (the camera–head
 * distance) at f/5.6 (a hyperfocal-ish far field: ≈ 4 px of blur at infinity
 * over 1920); the "cinematic" lens (dialogue, the traveler screen, a photo)
 * opens to f/2.
 *
 * SHUTTER. 180° (half the frame) — the motion-blur node's `shutterFraction`.
 * EXPOSURE. EV100 = log2(N² / t) − log2(ISO / 100); `evOffset` is this camera
 * against the reference (f/5.6, 1/120 s, ISO 100) — the lighting agent's
 * exposure (`__exposure()`) may add it; 0 by default.
 */
import { cocInfinityPx, physicalDofParams, type LensParams } from 'enginex/render/dof/PhysicalDof.js';

export interface CameraModel {
  /** f-number. */
  fNumber: number;
  /** Shutter ANGLE in degrees (180 = half the frame). */
  shutterDeg: number;
  /** Sensor sensitivity. */
  iso: number;
  /** Focal length (mm) of the DOF lens. */
  focalMm: number;
  /** Sensor width (mm). */
  sensorMm: number;
}

export const REFERENCE_CAMERA: CameraModel = { fNumber: 5.6, shutterDeg: 180, iso: 100, focalMm: 35, sensorMm: 36 };
/** The cinematic lens (dialogue / traveler screen / photo). */
export const CINEMATIC_F_NUMBER = 2;
export const FIRST_PERSON_VFOV = 60;
export const THIRD_PERSON_VFOV = 55;
/** The sprint / dash FOV kick never exceeds this (degrees). */
export const FOV_KICK_CAP = 3;
/** The aspect the menu's HORIZONTAL degrees refer to. */
export const REFERENCE_ASPECT = 16 / 9;

const D2R = Math.PI / 180, R2D = 180 / Math.PI;
/** Horizontal FOV (deg) of a vertical FOV at an aspect. */
export function hFovFromV(vDeg: number, aspect: number): number { return 2 * Math.atan(Math.tan((vDeg * D2R) / 2) * aspect) * R2D; }
/** Vertical FOV (deg) of a horizontal FOV at an aspect. */
export function vFovFromH(hDeg: number, aspect: number): number { return 2 * Math.atan(Math.tan((hDeg * D2R) / 2) / aspect) * R2D; }

/** The vertical FOV the rig uses: the player's stored one (first person; third person 5° narrower), else the defaults. */
export function baseVerticalFov(userVDeg: number | undefined, thirdPerson: boolean): number {
  const u = userVDeg ?? 0;
  if (u > 0) return thirdPerson ? Math.max(30, u - (FIRST_PERSON_VFOV - THIRD_PERSON_VFOV)) : u;
  return thirdPerson ? THIRD_PERSON_VFOV : FIRST_PERSON_VFOV;
}
/** The kick added this frame, capped. */
export function cappedFovKick(kickDeg: number): number { return Math.max(-FOV_KICK_CAP, Math.min(FOV_KICK_CAP, kickDeg)); }

/** The shutter as the fraction of each frame the blur spans. */
export function shutterFraction(cam: CameraModel): number { return Math.min(1, Math.max(0, cam.shutterDeg / 360)); }
/** Exposure time (s) of the shutter at a frame rate. */
export function shutterSeconds(cam: CameraModel, fps: number): number { return shutterFraction(cam) / Math.max(1, fps); }
/** EV100 of the camera at a frame rate. */
export function ev100(cam: CameraModel, fps = 60): number {
  return Math.log2((cam.fNumber * cam.fNumber) / shutterSeconds(cam, fps)) - Math.log2(cam.iso / 100);
}
/** This camera's exposure offset in EV against the reference (positive = the camera gathers LESS light). */
export function evOffset(cam: CameraModel, fps = 60): number { return ev100(cam, fps) - ev100(REFERENCE_CAMERA, fps); }

/** What the post pipeline's live DOF takes this frame (bokehScale 0 = sharp everywhere). */
export function dofFor(state: { thirdPerson: boolean; focusM: number; mode: 'off' | 'thirdPerson'; cinematic: boolean }, cam: CameraModel, imageWidthPx: number): { focusDistance: number; focusRange: number; bokehScale: number } {
  const lens: LensParams = { fNumber: state.cinematic ? CINEMATIC_F_NUMBER : cam.fNumber, focalMm: cam.focalMm, sensorMm: cam.sensorMm };
  if (!state.cinematic && (state.mode === 'off' || !state.thirdPerson)) return { focusDistance: Math.max(0.5, state.focusM), focusRange: 1e4, bokehScale: 0 };
  const p = physicalDofParams(Math.max(0.5, state.focusM), lens, imageWidthPx);
  return { focusDistance: p.focusDistance, focusRange: p.focusRange, bokehScale: p.bokehScale };
}
export { cocInfinityPx };

/** The live model (the menu's f-number writes it; `setCinematic` is the dialogue / traveler hook). */
export const cameraModel: CameraModel = { ...REFERENCE_CAMERA };
let cinematic = false;
export function setCinematic(on: boolean): void { cinematic = on; }
export function isCinematic(): boolean { return cinematic; }
